import { useState } from 'react';
import { useAuthActions } from '@convex-dev/auth/react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { TInviteAuthMode } from '@/@types/organisation';

interface IUseInviteAuthArgs {
  email: string;
  token: string;
  mode: TInviteAuthMode;
  onAccepted: () => void;
}

export function useInviteAuth({ email, token, mode, onAccepted }: IUseInviteAuthArgs) {
  const { signIn } = useAuthActions();
  const acceptInvitation = useMutation(api.organisations.invitations.acceptInvitation);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (mode === 'signUp' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsPending(true);
    try {
      await signIn('password', { email, password, flow: mode });
      const result = await acceptInvitation({ token });
      if (result.success) {
        onAccepted();
      } else {
        setError(result.error);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('InvalidAccountId') || msg.includes('Could not find')) {
        setError("This account doesn't exist. Try creating a new account instead.");
      } else if (
        msg.includes('InvalidSecret') ||
        msg.includes('InvalidPassword') ||
        msg.includes('incorrect')
      ) {
        setError('Incorrect password. Please try again.');
      } else if (msg.includes('already exists')) {
        setError('This account already exists. Try signing in instead.');
      } else {
        setError(msg || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsPending(false);
    }
  };

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    error,
    isPending,
    handleSubmit,
  };
}
